import { useCallback } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiArrowExpand, mdiContentCopy } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { $api } from '../../api/queryClient'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet'
import { Button } from '../ui/button'
import { IssueBody } from './IssueBody'

type Props = {
    issueUid: string | null
    onClose: () => void
}

export function IssueSheet({ issueUid, onClose }: Props) {
    const { org } = useParams({ strict: false })
    const orgCode = org || 'demo'
    const navigate = useNavigate()
    const { t } = useTranslation('issues')

    const enabled = Boolean(issueUid)
    // Light query just for the accessible name + target-button label.
    const { data: issue } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/issues/{issueUid}',
        {
            params: { path: { orgCode, issueUid: issueUid || '' } },
        },
        { enabled },
    )

    const copyLink = useCallback(() => {
        if (typeof navigator !== 'undefined' && navigator.clipboard) {
            void navigator.clipboard.writeText(window.location.href)
        }
    }, [])

    const openFullPage = useCallback(() => {
        if (!issueUid) return
        const from = encodeURIComponent(window.location.pathname + window.location.search)
        navigate({
            to: '/$org/issues/$uid',
            params: { org: orgCode, uid: issueUid },
            search: { from } as Record<string, unknown>,
        })
    }, [issueUid, navigate, orgCode])

    const openTarget = useCallback(() => {
        if (!issue?.target_entity_type || !issue.target_entity_uid) return
        onClose()
        const uid = issue.target_entity_uid
        switch (issue.target_entity_type) {
            case 'asset':
                // issue.type is typed as 'general' | 'compliance' in the v1
                // schema, but the backend's approval-issue-sync job writes
                // type='approval'. Compare via a widened cast.
                navigate({
                    to: '/$org/assets/$assetUid',
                    params: { org: orgCode, assetUid: uid },
                    search: (issue.type as string) === 'approval' ? ({ tab: 'identity' } as Record<string, unknown>) : undefined,
                })
                break
            case 'user':
                navigate({ to: '/$org/users/$userUid', params: { org: orgCode, userUid: uid } })
                break
            case 'contract':
                navigate({ to: '/$org/contracts/$contractUid', params: { org: orgCode, contractUid: uid } })
                break
            case 'document':
                navigate({ to: '/$org/documents/$docUid', params: { org: orgCode, docUid: uid } })
                break
            case 'intervention':
                navigate({ to: '/$org/interventions/$interventionUid', params: { org: orgCode, interventionUid: uid } })
                break
        }
    }, [issue, onClose, navigate, orgCode])

    return (
        <Sheet
            open={enabled}
            onOpenChange={(open) => {
                if (!open) onClose()
            }}
        >
            <SheetContent
                side="right"
                className="sm:max-w-xl flex flex-col"
                data-testid="issue-sheet"
                onEscapeKeyDown={(event) => {
                    // Let Escape cancel an inline edit first (Radix would otherwise
                    // close the sheet because its Escape listener runs in document-capture,
                    // before our Input's onKeyDown).
                    const target = event.target as HTMLElement | null
                    if (
                        target &&
                        (target.tagName === 'INPUT' ||
                            target.tagName === 'TEXTAREA' ||
                            target.isContentEditable)
                    ) {
                        event.preventDefault()
                    }
                }}
            >
                <SheetHeader>
                    <SheetTitle className="sr-only">
                        {issue?.title || t('sheet.loading', { defaultValue: 'Loading…' })}
                    </SheetTitle>
                    <div className="flex items-center gap-2 pr-8">
                        <div className="flex-1" />
                        <Button
                            variant="ghost"
                            size="icon"
                            title={t('sheet.open_full_page', { defaultValue: 'Open full page' })}
                            onClick={openFullPage}
                            disabled={!issueUid}
                            data-testid="issue-sheet-open-full"
                        >
                            <AppIcon path={mdiArrowExpand} size="sm" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            title={t('sheet.copy_link', { defaultValue: 'Copy link' })}
                            onClick={copyLink}
                            data-testid="issue-sheet-copy-link"
                        >
                            <AppIcon path={mdiContentCopy} size="sm" />
                        </Button>
                    </div>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto px-4 py-2">
                    {issueUid && <IssueBody orgCode={orgCode} issueUid={issueUid} compact />}
                </div>

                <div className="flex items-center justify-between gap-2 border-t border-border p-4">
                    {issue?.target_entity_type && issue?.target_entity_uid ? (
                        <Button
                            variant="default"
                            onClick={openTarget}
                            data-testid="issue-sheet-open-target"
                        >
                            {t('sheet.open_target', {
                                type: t(`targetEntityType.${issue.target_entity_type}`, {
                                    defaultValue: issue.target_entity_type,
                                }),
                                defaultValue: `Open ${issue.target_entity_type}`,
                            })}
                        </Button>
                    ) : (
                        <span />
                    )}
                    <Button variant="outline" onClick={onClose} data-testid="issue-sheet-close">
                        {t('sheet.close', { defaultValue: 'Close' })}
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    )
}
